import React, { useState, useMemo } from 'react';
import { Wind } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { NumberField, GenderField, SelectField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { vo2maxCooper, vo2maxFromHR, getVO2MaxCategory } from '../../lib/calculatorUtils';

const VO2MaxCalculatorPage = () => {
  const [method, setMethod] = useState('cooper');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('male');
  const [distance, setDistance] = useState('');
  const [resting, setResting] = useState('');

  const result = useMemo(() => {
    if (method === 'cooper') {
      const d = parseFloat(distance);
      if (!d || d < 200) return null;
      const v = vo2maxCooper(d);
      if (!v || v <= 0) return null;
      const a = parseInt(age, 10) || 30;
      return { vo2: v, category: getVO2MaxCategory(v, a, gender) };
    }
    const a = parseInt(age, 10);
    const r = parseInt(resting, 10);
    if (!a || !r || a < 10 || r <= 30) return null;
    const v = vo2maxFromHR(a, r);
    if (!v) return null;
    return { vo2: v, category: getVO2MaxCategory(v, a, gender) };
  }, [method, age, gender, distance, resting]);

  const inputs = (
    <div className="space-y-5" data-testid="vo2-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Estimation Method</h2>
      <p className="text-slate-400 text-sm mb-5">Pick a method below.</p>
      <SelectField id="vo2-method" label="Method" value={method} onChange={setMethod} options={[
        { value: 'cooper', label: 'Cooper 12-min run test' },
        { value: 'hr', label: 'Resting heart rate estimate' }
      ]} testId="vo2-method-select" />
      <GenderField value={gender} onChange={setGender} testIdPrefix="vo2-gender" />
      <NumberField id="vo2-age" label="Age" value={age} onChange={setAge} placeholder="e.g. 28" suffix="yrs" min={10} max={120} testId="vo2-age-input" required />
      {method === 'cooper' ? (
        <NumberField id="vo2-distance" label="Distance covered in 12 minutes" value={distance} onChange={setDistance} placeholder="e.g. 2400" suffix="metres" min={200} max={5000} testId="vo2-distance-input" required />
      ) : (
        <NumberField id="vo2-resting" label="Resting Heart Rate" value={resting} onChange={setResting} placeholder="e.g. 60" suffix="bpm" min={30} max={120} testId="vo2-resting-input" required />
      )}
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="VO₂ Max" value={result.vo2} unit="ml/kg/min" accent="from-cyan-500 to-blue-500" category={result.category} testId="vo2-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Category" value={result.category.label} color={result.category.color} testId="vo2-category" />
        <StatRow label="Method" value={method === 'cooper' ? 'Cooper 12-min run' : 'Heart Rate estimate'} />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        VO₂ Max is a key indicator of cardiovascular fitness. Field tests are estimates, and laboratory testing is more accurate.
      </p>
    </div>
  ) : (
    <EmptyResult message="Choose a method and enter the required inputs to estimate VO₂ Max." />
  );

  return (
    <CalculatorLayout
      slug="vo2-max" title="VO₂ Max Calculator"
      subtitle="Estimate your aerobic capacity using the Cooper test or your resting heart rate."
      metaDescription="VO₂ Max Calculator to estimate aerobic fitness using the Cooper 12-min test or resting heart rate."
      icon={Wind}
      accentGradient="from-cyan-500 to-blue-500" accentColor="text-cyan-400"
      badgeBg="bg-cyan-500/10" badgeBorder="border-cyan-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Cooper:</strong> VO₂ Max = (distance in metres − 504.9) ÷ 44.73</p>
        <p><strong className="text-slate-300">Resting HR:</strong> VO₂ Max ≈ 15.3 × (HRmax ÷ HRrest)</p>
      </>}
    />
  );
};

export default VO2MaxCalculatorPage;
